import { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Alert,
    Paper,
    CircularProgress,
    List,
    ListItem,
    ListItemText,
    Divider
} from '@mui/material';
import { Upload as UploadIcon } from '@mui/icons-material';
import api from '../../services/api';

const ImportChildren = () => {
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);

    const handleFileChange = (event) => {
        const selectedFile = event.target.files[0];
        if (selectedFile && selectedFile.type === 'application/json') {
            setFile(selectedFile);
            setError(null);
        } else {
            setError('Please select a valid JSON file');
            setFile(null);
        }
    };

    const handleUpload = async () => {
        if (!file) return;

        try {
            setLoading(true);
            setError(null);

            // Read the file content
            const fileContent = await file.text();
            const children = JSON.parse(fileContent);

            // Send to backend
            const response = await api.post('/admin/children/import', {
                children
            });

            setResult(response.data);
        } catch (error) {
            setError(error.response?.data?.message || 'Failed to import children');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            <Typography variant="h4" gutterBottom>
                Import Children
            </Typography>

            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="body1" gutterBottom>
                    Upload a JSON file containing children information. The file should contain an array of children with the following fields:
                </Typography>
                <List dense>
                    <ListItem>
                        <ListItemText primary="FirstName" secondary="Child's first name" />
                    </ListItem>
                    <ListItem>
                        <ListItemText primary="LastName" secondary="Child's last name" />
                    </ListItem>
                    <ListItem>
                        <ListItemText primary="DateOfBirth" secondary="Child's date of birth (MM/DD/YYYY)" />
                    </ListItem>
                    <ListItem>
                        <ListItemText primary="Gender" secondary="Child's gender (Male/Female)" />
                    </ListItem>
                </List>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Button
                        variant="contained"
                        component="label"
                        disabled={loading}
                    >
                        Select File
                        <input
                            type="file"
                            hidden
                            accept="application/json"
                            onChange={handleFileChange}
                        />
                    </Button>
                    {file && (
                        <Typography variant="body2" color="textSecondary">
                            Selected: {file.name}
                        </Typography>
                    )}
                </Box>
                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}
                {file && (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleUpload}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : <UploadIcon />}
                        sx={{ mt: 2 }}
                    >
                        Upload and Import
                    </Button>
                )}
                {result && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                        Successfully imported {result.imported} children.
                        {result.skipped > 0 && ` Skipped ${result.skipped} duplicate entries.`}
                        {result.skippedInvalid > 0 && ` Skipped ${result.skippedInvalid} invalid entries.`}
                    </Alert>
                )}
            </Paper>
        </Box>
    );
};

export default ImportChildren; 